import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FullPageValidation } from '@/components/ui/full-page-validation';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { transformToValidationData, type ValidationData } from '@/utils/validation-transformer';
import { ArrowLeft, Loader2 } from 'lucide-react';

const ValidationResults: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user && id) {
      fetchValidationData();
    }
  }, [user, id]);

  const fetchValidationData = async () => {
    if (!user || !id) return;
    
    setLoading(true);
    try {
      // First get the idea data
      const { data: ideaData, error: ideaError } = await supabase
        .from('idea_assessments')
        .select('*')
        .eq('user_id', user.id)
        .eq('id', id)
        .single();

      if (ideaError) throw ideaError;

      if (!ideaData) {
        setError('Idea not found.');
        return;
      }

      // Get AI feedback from the idea_assessments table
      let feedbackText = ideaData.ai_feedback;

      if (feedbackText) {
        const ideaFormData = {
          idea_description: ideaData.idea_description,
          target_audience: ideaData.target_audience,
          problem_solved: ideaData.problem_solved,
          revenue_model: ideaData.revenue_model,
          competition: ideaData.competition,
          why_pay: (ideaData as any).why_people_pay,
        };

        const transformedData = transformToValidationData(ideaFormData, feedbackText as any);
        setValidationData(transformedData);
      } else {
        setError('No validation data found for this idea.');
      }
    } catch (err) {
      console.error('Error fetching validation data:', err);
      setError('Failed to load validation results.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToValidation = () => {
    // Navigate back to the idea validation form
    navigate('/app/idea-validation', { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading validation results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={handleBackToValidation} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Idea Validation
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Full-Screen Validation Dashboard */}
      {validationData && (
        <FullPageValidation data={validationData} onBack={handleBackToValidation} />
      )}
    </>
  );
};

export default ValidationResults;
